<template>
  <Teleport to="body">
    <div v-if="open" class="lg-modal-backdrop" @pointerdown.self="onBackdrop">
      <div
        ref="panel"
        class="lg-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="ariaLabel"
        :aria-labelledby="title ? titleId : undefined"
        @keydown="onKeydown"
      >
        <header v-if="title || $slots.header" class="lg-modal-head">
          <slot name="header">
            <h2 :id="titleId" class="lg-modal-title">{{ title }}</h2>
          </slot>
          <LgIconButton label="Close" size="sm" @click="$emit('close')">
            <X :size="18" aria-hidden="true" />
          </LgIconButton>
        </header>

        <div class="lg-modal-body">
          <slot />
        </div>

        <footer v-if="$slots.footer" class="lg-modal-foot">
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import { X } from 'lucide-vue-next';
import LgIconButton from './LgIconButton.vue';
import { useFocusTrap } from './useFocusTrap.js';
import { useScrollLock } from './useScrollLock.js';

const props = withDefaults(
  defineProps<{
    open: boolean;
    title?: string;
    ariaLabel?: string;
    closeOnBackdrop?: boolean;
  }>(),
  { closeOnBackdrop: true },
);

const emit = defineEmits<{ close: [] }>();

const titleId = `lgmodal-${Math.floor(Math.random() * 1e6)}`;
const panel = ref<HTMLElement | null>(null);
let lastFocused: HTMLElement | null = null;

const trap = useFocusTrap(panel);

// Ref-counted body scroll lock — shared with LgDrawer. Modals had no scroll
// lock at all before this, which was inconsistent given they're just as
// modal as the drawer; unmount-while-open is handled inside the composable.
const scrollLock = useScrollLock();

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    e.preventDefault();
    emit('close');
    return;
  }
  trap.onKeydown(e);
}

function onBackdrop() {
  if (props.closeOnBackdrop) emit('close');
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      scrollLock.lock();
      lastFocused = document.activeElement as HTMLElement;
      nextTick(() => trap.focusFirst());
    } else {
      scrollLock.unlock();
      lastFocused?.focus?.();
      lastFocused = null;
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.lg-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--lg-z-modal-backdrop);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--lg-space-4);
  background: var(--lg-scrim);
}
.lg-modal {
  z-index: var(--lg-z-modal);
  width: 100%;
  max-width: 440px;
  max-height: calc(100vh - var(--lg-space-8));
  display: flex;
  flex-direction: column;
  border-radius: var(--lg-radius-lg);
  border: 1px solid var(--lg-border);
  background: var(--lg-bg-raised);
  box-shadow: var(--lg-shadow-3);
}
.lg-modal:focus {
  outline: none;
}
.lg-modal-head {
  /* Header and footer never shrink, so the close affordance and the primary
     action stay pinned/reachable while only the body scrolls. */
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--lg-space-3);
  padding: var(--lg-space-4) var(--lg-space-5);
  border-bottom: 1px solid var(--lg-border-faint);
}
.lg-modal-title {
  margin: 0;
  font-size: var(--lg-fs-lg);
  font-weight: 600;
}
.lg-modal-body {
  /* `min-height: 0` lets this flex child shrink below its content height so
     overflow scrolls *inside* the body rather than blowing past the modal's
     max-height and pushing the footer off-screen. */
  flex: 1 1 auto;
  min-height: 0;
  padding: var(--lg-space-5);
  overflow-y: auto;
  overflow-x: hidden;
}
.lg-modal-foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--lg-space-2);
  padding: var(--lg-space-4) var(--lg-space-5);
  border-top: 1px solid var(--lg-border-faint);
}

/* Mobile: every modal becomes a full-width bottom sheet pinned to the bottom
   edge, never positioned off-screen regardless of content size. It scrolls on
   a single axis (the body's overflow-y) — horizontal scrolling is deliberately
   impossible (overflow-x: hidden below + on .lg-modal-body). `dvh` tracks the
   *visible* viewport so the sheet isn't clipped by mobile browser chrome, and
   the safe-area inset keeps the footer/close clear of the home indicator. */
@media (max-width: 768px) {
  .lg-modal-backdrop {
    padding: 0;
    align-items: flex-end;
  }
  .lg-modal {
    width: 100%;
    max-width: none;
    max-height: 90dvh;
    overflow-x: hidden;
    border-radius: var(--lg-radius-lg) var(--lg-radius-lg) 0 0;
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
  .lg-modal-body {
    overflow-y: auto;
    overflow-x: hidden;
  }
}
</style>
